// assets/component-swiper-slider.js
(() => {
  class SwiperSlider extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        swiper: "swiper-container",
        prevEl: ".custom-prev-button",
        nextEl: ".custom-next-button",
        pagination: ".custom-pagination-element",
        scrollbar: ".custom-scrollbar-element",
      };
      this._onSectionLoad = this._onSectionLoad.bind(this);
      this._onSectionSelect = this._onSectionSelect.bind(this);
      this._onSectionDeselect = this._onSectionDeselect.bind(this);
      this._onSectionReorder = this._onSectionReorder.bind(this);
      this._onSectionUnload = this._onSectionUnload.bind(this);
      this._debouncedReinit = this._debounce(() => this.initSlider(true), 120);
    }

    async connectedCallback() {
      try {
        await this._ensureSwiperLoaded();
        await new Promise(r => requestAnimationFrame(r)); // let upgrades happen
        this._mount();
        this._attachEditorEvents();
        this._observeMutations();
      } catch (err) {
        console.warn('Failed to load Swiper Element', err);
      }
    }

    disconnectedCallback() {
      this._detachEditorEvents();
      if (this._observer) this._observer.disconnect();
      this.destroySlider();
    }

    _mount() {
      this.slider = this.querySelector(this.selectors.swiper);
      this.buttonPrev = this.querySelector(this.selectors.prevEl);
      this.buttonNext = this.querySelector(this.selectors.nextEl);
      this.pagination = this.querySelector(this.selectors.pagination);
      this.scrollbar = this.querySelector(this.selectors.scrollbar);
      if (!this.slider) return;
      this.initSlider(false);
    }

    _remountSoon() {
      requestAnimationFrame(() => requestAnimationFrame(() => this._mount()));
    }

    _sectionEl() {
      return this.closest('[data-section-id]') || this.closest('.shopify-section');
    }
    _isMySection(evt) {
      const id = evt?.detail?.sectionId;
      const el = this._sectionEl();
      if (!id || !el) return false;
      return el.id?.includes(id) || el.dataset.sectionId === String(id);
    }

    _attachEditorEvents() {
      document.addEventListener('shopify:section:load', this._onSectionLoad);
      document.addEventListener('shopify:section:select', this._onSectionSelect);
      document.addEventListener('shopify:section:deselect', this._onSectionDeselect);
      document.addEventListener('shopify:section:reorder', this._onSectionReorder);
      document.addEventListener('shopify:section:unload', this._onSectionUnload);
    }
    _detachEditorEvents() {
      document.removeEventListener('shopify:section:load', this._onSectionLoad);
      document.removeEventListener('shopify:section:select', this._onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this._onSectionDeselect);
      document.removeEventListener('shopify:section:reorder', this._onSectionReorder);
      document.removeEventListener('shopify:section:unload', this._onSectionUnload);
    }

    _onSectionLoad(e){ if (this._isMySection(e)) this._remountSoon(); }
    _onSectionSelect(e){ if (this._isMySection(e)) this._remountSoon(); }
    _onSectionDeselect(e){ if (this._isMySection(e)) this._remountSoon(); }
    _onSectionReorder(e){ if (this._isMySection(e)) this._remountSoon(); }
    _onSectionUnload(e){ if (this._isMySection(e)) this.destroySlider(); }

    _observeMutations() {
      if (this._observer) this._observer.disconnect();
      this._observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (
            (m.type === 'attributes' && m.attributeName === 'data-options' && m.target === this.slider) ||
            (m.type === 'childList')
          ) { this._debouncedReinit(); break; }
        }
      });
      this._observer.observe(this, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['data-options'],
      });
    }

    _ensureSwiperLoaded() {
      if (customElements.get('swiper-container')) return Promise.resolve();
      if (window.__swiperElementPromise) return window.__swiperElementPromise;

      window.__swiperElementPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'module';
        script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-element-bundle.min.js';
        script.onload = () => customElements.whenDefined('swiper-container').then(resolve);
        script.onerror = reject;
        document.head.appendChild(script);
      });
      return window.__swiperElementPromise;
    }

    destroySlider() {
      try { if (this.slider?.swiper) this.slider.swiper.destroy(true, true); } catch(_) {}
      this?.querySelectorAll?.('.swiper-wrapper, .swiper-slide')?.forEach(n => n.removeAttribute('style'));
    }

    async initSlider(force = true) {
      if (force) this.destroySlider();

      // re-query in case of DOM swap
      this.slider = this.querySelector(this.selectors.swiper);
      if (!this.slider) return;

      this.buttonPrev = this.querySelector(this.selectors.prevEl);
      this.buttonNext = this.querySelector(this.selectors.nextEl);
      this.pagination = this.querySelector(this.selectors.pagination);
      this.scrollbar = this.querySelector(this.selectors.scrollbar);

      if (typeof this.slider.initialize !== 'function') {
        await customElements.whenDefined('swiper-container');
        await new Promise(r => requestAnimationFrame(r));
      }

      // 1) read inline options from THIS instance only
      let inlineOptions = this.slider.dataset.options;
      if (inlineOptions) {
        try { inlineOptions = JSON.parse(inlineOptions); }
        catch (e) { console.warn('Invalid data-options JSON', e, inlineOptions); inlineOptions = {}; }
      } else {
        inlineOptions = {};
      }
      inlineOptions = this._coerceNumbers(inlineOptions);

      // 2) only wire up plumbing; DO NOT set slidesPerView/breakpoints unless
      //    the inline config omitted them (keeps other sections intact)
      const plumbing = {
        pagination: this.pagination ? { el: this.pagination, type: 'progressbar' } : undefined,
        navigation: (this.buttonPrev && this.buttonNext) ? { nextEl: this.buttonNext, prevEl: this.buttonPrev } : undefined,
        scrollbar: this.scrollbar ? { el: this.scrollbar, hide: false } : undefined,
        on: { init(){} }
      };

      // If no view params provided at all, give a tiny safe mobile fallback (won't affect others who pass options)
      const hasViewParams = ('slidesPerView' in inlineOptions) || ('breakpoints' in inlineOptions);
      const safeFallback = hasViewParams ? {} : { slidesPerView: 1, spaceBetween: 12 };

      // 3) assign in order: fallback → inline → plumbing
      Object.assign(this.slider, safeFallback, inlineOptions, plumbing);

      // 4) init/update
      if (!this.slider.swiper) this.slider.initialize();
      else this.slider.swiper.update();
    }

    _debounce(fn, wait) { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, a), wait); }; }

    _coerceNumbers(obj){
      if (obj == null || typeof obj !== 'object') return obj;
      if (Array.isArray(obj)) return obj.map(v => this._coerceNumbers(v));
      return Object.fromEntries(Object.entries(obj).map(([k,v]) => {
        if (typeof v === 'string' && v.trim() !== '' && !isNaN(v)) return [k, Number(v)];
        if (typeof v === 'object') return [k, this._coerceNumbers(v)];
        return [k,v];
      }));
    }
  }

  if (!customElements.get('component-swiper-slider')) {
    customElements.define('component-swiper-slider', SwiperSlider);
  }
})();